import { Component, EventEmitter, Input, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AddEvent, CancelEvent, EditEvent, GridComponent, RemoveEvent, SaveEvent } from '@progress/kendo-angular-grid';
import { Observable, Subscription } from 'rxjs';
import { PatchPartElementDto } from '../../../interfaces/request.interface';

@Component({
    selector: 'app-partelement-data',
    templateUrl: './partelement-data.component.html',
    styleUrl: './partelement-data.component.css',
    standalone: false
})
export class PartelementDataComponent implements OnDestroy {
  @Input() partTitle: string = '';
  @Input() partKey: string = '';
  @Input() picklistOptions: any[] = [];
  @Input() elementsData$!: Observable<any[]>;
  @Input() isModelDriven: boolean = false;
  @Input() isMultiRow: boolean = false;
  @Input() isRTADriven: boolean = false;
  @Input() canAddRow: boolean = false;


  @Output() elementsUpdated = new EventEmitter<{ partKey: string; elements: PatchPartElementDto[] }>();
  private editedRowIndex: number | undefined;
  public formGroup!: FormGroup;
  private isEditing = false;
  public isNewRow = false;

  createdData: PatchPartElementDto[] = [];
  updatedData: PatchPartElementDto[] = [];
  deletedData: PatchPartElementDto[] = [];

  localElementsData: any[] = [];
  private isInitialized = false;
  private dataSubscription?: Subscription;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['elementsData$'] && this.elementsData$) {
      // Unsubscribe from previous subscription to avoid memory leaks
      this.dataSubscription?.unsubscribe();

      this.dataSubscription = this.elementsData$.subscribe(data => {
        // Don't update localElementsData while the user is actively editing a row
        if (this.isEditing) {
          return;
        }
        // Seed localElementsData from the observable on first load or when no pending edits exist
        if (this.createdData.length === 0 && this.updatedData.length === 0 && this.deletedData.length === 0) {
          this.localElementsData = JSON.parse(JSON.stringify(data));
        }
        this.isInitialized = true;
      });
    }
  }

  ngOnDestroy() {
    this.dataSubscription?.unsubscribe();
  }

  editHandler({ sender, rowIndex, dataItem }: EditEvent): void {
    this.closeEditor(sender);
    this.formGroup = createFormGroup(dataItem);
    this.editedRowIndex = rowIndex;
    this.isEditing = true;
    this.isNewRow = false;
    sender.editRow(rowIndex, this.formGroup);
    // Don't trigger save during edit - wait for actual save
  }

  cancelHandler(args: CancelEvent): void {
    this.closeEditor(args.sender, args.rowIndex);
    this.isEditing = false;
    this.isNewRow = false;
    // No need to trigger save on cancel
  }

  saveHandler({ sender, rowIndex, formGroup, isNew }: SaveEvent): void {
    const _saveData = formGroup.value;

    // Round quantity to 3 decimal places before saving
    if (_saveData.quantity != null) {
      _saveData.quantity = Math.round(_saveData.quantity * 1000) / 1000;
    }

    if (isNew) {
      this.localElementsData = [_saveData, ...this.localElementsData];
      // Mark as created if id is 0 or undefined
      if (!_saveData.id || _saveData.id === 0) {
        this.createdData.push(_saveData);
      }
    } else {
      const index = this.localElementsData.findIndex(el => el.id === _saveData.id);
      if (index !== -1) {
        const updated = [...this.localElementsData];
        updated[index] = _saveData;
        this.localElementsData = updated;
        // Only add to updated if not already in created
        if (!this.createdData.find(item => item.id === _saveData.id)) {
          // Remove from updatedData if already there to avoid duplicates
          this.updatedData = this.updatedData.filter(item => item.id !== _saveData.id);
          this.updatedData.push(_saveData);
        }
      }
    }
    sender.closeRow(rowIndex);
    this.isEditing = false;
    this.isNewRow = false;
    // Trigger save AFTER the data is updated
    this.triggerSave();
  }

  removeHandler(args: RemoveEvent) {
    this.localElementsData = this.localElementsData.filter(el => el.id !== args.dataItem.id);
    
    // If the item was just created (in createdData), remove it from there
    const createdIndex = this.createdData.findIndex(item => item.id === args.dataItem.id);
    if (createdIndex !== -1) {
      this.createdData.splice(createdIndex, 1);
    } else if (!this.updatedData.find(item => item.id === args.dataItem.id)) {
      // Only add to deletedData if it wasn't just created
      this.deletedData.push(args.dataItem);
    }
    
    // Trigger save AFTER removal
    this.triggerSave();
  }

  addHandler(args: AddEvent) {
    this.closeEditor(args.sender);
    this.formGroup = createFormGroup({
      id: 0,
      preBOMID: 0,
      type: '',
      quantity: 0,
      sapMaterialNumber: 0
    })

    this.isEditing = true;
    this.isNewRow = true;
    args.sender.addRow(this.formGroup);
    // Don't trigger save during add - wait for actual save
  }

  onTypeSelectionChange(selectedValue: number, formGroup: FormGroup): void {
    // Find the matching picklist option to get the display text
    const selectedOption = this.picklistOptions.find(opt => opt.value === selectedValue);
    if (selectedOption) {
      formGroup.get('type')?.setValue(selectedOption.text);
    }
  }

  private closeEditor(
    grid: GridComponent,
    rowIndex = this.editedRowIndex
  ): void {
    grid.closeRow(rowIndex);
  }

  triggerSave() {
    const combinedChanges = [
      ...this.createdData,
      ...this.updatedData,
      ...this.deletedData
    ];
    
    console.log('Triggering save for partKey:', this.partKey);
    console.log('Created:', this.createdData);
    console.log('Updated:', this.updatedData);
    console.log('Deleted:', this.deletedData);
    console.log('Combined changes:', combinedChanges);
    
    if (combinedChanges.length > 0) {
      this.elementsUpdated.emit({
        partKey: this.partKey,
        elements: combinedChanges
      });
    }

    // Don't clear arrays here - they should persist across saves
    // Only clear after successful API response from parent
  }

  // Public method to clear the change tracking (call from parent after successful save)
  public clearChanges() {
    this.createdData = [];
    this.updatedData = [];
    this.deletedData = [];
  }
}
const createFormGroup = (dataItem: PatchPartElementDto) => {
  const formGroup = new FormGroup({
    id: new FormControl(dataItem.id),
    preBOMID: new FormControl(dataItem.preBOMID),
    type: new FormControl(dataItem.type),
    sapMaterialNumber: new FormControl(dataItem.sapMaterialNumber),
    quantity: new FormControl(dataItem.quantity, Validators.compose([
      Validators.required,
      Validators.min(0)
    ]))
  });
  return formGroup;
};
